import type {
	GetOnboardingWelcomeQuery,
	OnboardingWelcomePreferenceFragment
} from "~/gql/queries/onboardingJourney.generated";

import {
	OnboardingJourneyNextActionKind,
	OnboardingJourneyOperationStatus,
	OnboardingJourneyQuestionState,
	OnboardingJourneyScopeStatus,
	OnboardingPlanNextAction,
	OnboardingSetupStage
} from "~/gql/graphqlBase.generated";
import onboardingTraining from "~/helpers/onboardingTraining";

export type WelcomeAudience = "continue" | "new" | "status";

export type NamedFamily = "customers" | "items" | "orders" | "pricing" | "terms";

export type WelcomeFamilyStatus = "deferred" | "done" | "inProgress" | "notChosen";

export interface WelcomeFamily {
	family: NamedFamily;
	status: WelcomeFamilyStatus;
}

export interface JourneyProgressSummary {
	accepted: number;
	families: WelcomeFamily[];
	loaded: number;
	nextStep: OnboardingJourneyNextActionKind | null;
	openQuestions: number;
	pending: number;
}

export interface WelcomeOpening {
	actionLabelKey: string;
	messageKey: string;
	startsJourney: boolean;
}

export interface WelcomeAudienceEvidence {
	dataIsIn: boolean;
	journeyExists: boolean;
	journeyHasOpenWork: boolean;
	journeyUnfinished: boolean;
	onboardingSetup: OnboardingSetupStage;
	trainingComplete: boolean;
}

export interface PlanFamilyEvidence {
	family: string;
	nextAction: OnboardingPlanNextAction;
}

export interface HomeSurfaces {
	fernieCard: boolean;
	welcomeWindow: boolean;
}

export interface ShownWelcomeInput {
	audience: WelcomeAudience | null;
	isAiChatEnabled: boolean;
	now: number;
	onboardingSetup: OnboardingSetupStage;
	preference: OnboardingWelcomePreferenceFragment;
}

type JourneyState = NonNullable<GetOnboardingWelcomeQuery["onboardingJourneyState"]>;

type JourneyProgress = JourneyState["progress"];

const NAMED_FAMILIES: NamedFamily[] = ["customers", "items", "orders", "pricing", "terms"];

const FAMILY_LABEL_KEY: Record<NamedFamily, string> = {
	customers: "home.fernieWelcome.family.customers",
	items: "home.fernieWelcome.family.items",
	orders: "home.fernieWelcome.family.orders",
	pricing: "home.fernieWelcome.family.pricing",
	terms: "home.fernieWelcome.family.terms"
};

const ACCEPTED_STATUSES: ReadonlySet<OnboardingJourneyOperationStatus> = new Set([
	OnboardingJourneyOperationStatus.CommittedUnverified,
	OnboardingJourneyOperationStatus.HeldByRun
]);

const OPENINGS: Record<WelcomeAudience, WelcomeOpening> = {
	continue: {
		actionLabelKey: "home.fernieWelcome.continue",
		messageKey: "home.fernieWelcome.continueMessage",
		startsJourney: false
	},
	new: {
		actionLabelKey: "home.fernieWelcome.getStarted",
		messageKey: "home.fernieWelcome.getStartedMessage",
		startsJourney: true
	},
	status: {
		actionLabelKey: "home.fernieWelcome.checkStatus",
		messageKey: "home.fernieWelcome.checkStatusMessage",
		startsJourney: false
	}
};

const UPLOADED_FAMILIES: NamedFamily[] = ["customers", "items", "pricing", "terms"];

const OPEN_WORK_ACTIONS: ReadonlySet<OnboardingJourneyNextActionKind> = new Set([
	OnboardingJourneyNextActionKind.AnswerQuestion,
	OnboardingJourneyNextActionKind.RecoverArtifact,
	OnboardingJourneyNextActionKind.ReviewPortion,
	OnboardingJourneyNextActionKind.VerifySelectedWork
]);

const isDataIn = (families: PlanFamilyEvidence[] | null | undefined): boolean =>
	!!families &&
	UPLOADED_FAMILIES.every((name) =>
		families.some((candidate) => candidate.family === name && candidate.nextAction === OnboardingPlanNextAction.Done)
	);

const hasOpenJourneyWork = (state: JourneyState | null | undefined): boolean =>
	!!state &&
	(state.questions.some((question) => question.state === OnboardingJourneyQuestionState.Open) ||
		state.progress.remainingWork.length > 0 ||
		state.progress.nextActions.some((action) => OPEN_WORK_ACTIONS.has(action.kind)));

const deriveAudience = ({
	dataIsIn,
	journeyExists,
	journeyHasOpenWork,
	journeyUnfinished,
	onboardingSetup,
	trainingComplete
}: WelcomeAudienceEvidence): WelcomeAudience | null => {
	if (journeyUnfinished && !(dataIsIn && !journeyHasOpenWork)) return "continue";
	if (!journeyExists && onboardingSetup === OnboardingSetupStage.NotStarted && !dataIsIn) return "new";
	return onboardingTraining.isStillOnboarding({ onboardingSetup, trainingComplete }) ? "status" : null;
};

const isSnoozeInForce = (preference: OnboardingWelcomePreferenceFragment, now: number): boolean =>
	preference.isSnoozed && (!preference.snoozedUntil || Date.parse(preference.snoozedUntil) > now);

const resolveShownWelcome = ({
	audience,
	isAiChatEnabled,
	now,
	onboardingSetup,
	preference
}: ShownWelcomeInput): WelcomeAudience | null => {
	if (audience === null || !isAiChatEnabled) return null;
	const snoozed = onboardingSetup !== OnboardingSetupStage.ReadyForReview && isSnoozeInForce(preference, now);
	if (preference.isDismissed || snoozed) return null;
	return audience;
};

const welcomeOpening = (audience: WelcomeAudience): WelcomeOpening => OPENINGS[audience];

const homeSurfaces = (audience: WelcomeAudience | null): HomeSurfaces => ({
	fernieCard: audience === null,
	welcomeWindow: audience !== null
});

const familyStatus = (family: NamedFamily, progress: JourneyProgress): WelcomeFamilyStatus => {
	const counts = progress.families.find((candidate) => candidate.family === family);
	if (counts && counts.selectedItemCount > 0)
		return counts.completeItemCount === counts.selectedItemCount ? "done" : "inProgress";
	const decisions = progress.scope.filter((item) => item.family === family);
	const isDeferred =
		decisions.length > 0 && decisions.every((item) => item.status === OnboardingJourneyScopeStatus.Deferred);
	return isDeferred ? "deferred" : "notChosen";
};

const summarizeJourneyProgress = ({ progress, questions }: JourneyState): JourneyProgressSummary => {
	const accepted = progress.remainingWork.filter((operation) => ACCEPTED_STATUSES.has(operation.status)).length;
	return {
		accepted,
		families: NAMED_FAMILIES.map((family) => ({ family, status: familyStatus(family, progress) })),
		loaded: progress.families.reduce((total, family) => total + family.completeOperationCount, 0),
		nextStep: progress.nextActions[0]?.kind ?? null,
		openQuestions: questions.filter((question) => question.state === OnboardingJourneyQuestionState.Open).length,
		pending: progress.remainingWork.length - accepted
	};
};

const familyLabelKey = (family: string): string | null => {
	const named = NAMED_FAMILIES.find((candidate) => candidate === family);
	return named ? FAMILY_LABEL_KEY[named] : null;
};

const onboardingWelcome = {
	deriveAudience,
	familyLabelKey,
	familyLabelKeys: FAMILY_LABEL_KEY,
	hasOpenJourneyWork,
	homeSurfaces,
	isDataIn,
	resolveShownWelcome,
	summarizeJourneyProgress,
	welcomeOpening
};

export default onboardingWelcome;
